import { useState } from 'react';
import { ArrowUpRight, Globe, Mail, MapPin, MessageCircle, Phone, Send } from 'lucide-react';
import { company, products, qrImage } from '../data/content';

const mapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(company.address)}`;

const details = [
  { Icon: Phone, label: 'Call us', value: company.phone, href: `tel:${company.phoneRaw}` },
  { Icon: Mail, label: 'Email', value: company.email, href: `mailto:${company.email}` },
  { Icon: Globe, label: 'Website', value: company.website, href: `https://${company.website}` },
  { Icon: MapPin, label: 'Visit', value: company.address, href: mapsLink },
];

const inputClass =
  'peer w-full border-0 border-b-2 border-gray-200 bg-transparent px-0 pb-2 pt-6 text-base text-ink outline-none transition placeholder:text-transparent focus:border-brand-600 focus:ring-0';
const labelClass =
  'pointer-events-none absolute left-0 top-1 text-xs font-semibold uppercase tracking-wider text-gray-400 transition-all peer-placeholder-shown:top-6 peer-placeholder-shown:text-sm peer-placeholder-shown:normal-case peer-placeholder-shown:tracking-normal peer-focus:top-1 peer-focus:text-xs peer-focus:uppercase peer-focus:tracking-wider peer-focus:text-brand-600';

function Field({ label, name, type = 'text', required, value, onChange }) {
  return (
    <label className="relative block">
      <input
        name={name}
        type={type}
        required={required}
        placeholder={label}
        value={value}
        onChange={onChange}
        className={inputClass}
      />
      <span className={labelClass}>
        {label}
        {required && ' *'}
      </span>
    </label>
  );
}

export default function Contact() {
  const [form, setForm] = useState({ name: '', phone: '', email: '', product: '', message: '' });
  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  // No backend yet: the enquiry opens in the visitor's mail app, addressed to the company.
  const onSubmit = (e) => {
    e.preventDefault();
    const subject = `Enquiry${form.product ? ` - ${form.product}` : ''} from ${form.name}`;
    const body = [
      `Name: ${form.name}`,
      `Phone: ${form.phone}`,
      `Email: ${form.email}`,
      `Interested in: ${form.product || '-'}`,
      '',
      form.message,
    ].join('\n');
    window.location.href = `mailto:${company.email}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  };

  return (
    <section id="contact" className="relative overflow-hidden bg-brand-600 py-24 text-white lg:py-32">
      <div className="grid-lines pointer-events-none absolute inset-0 opacity-60" />
      <div className="pointer-events-none absolute -bottom-10 right-0 select-none font-display text-[20vw] font-bold leading-none text-white/10 text-outline">
        H₂O
      </div>

      <div className="container-x relative grid gap-14 lg:grid-cols-2">
        <div className="reveal">
          <span className="eyebrow !text-white before:!bg-white">Contact Us</span>
          <h2 className="mt-4 text-5xl font-bold leading-[0.95] sm:text-7xl">
            Let’s talk
            <br />
            water.
          </h2>
          <p className="mt-6 max-w-md text-lg text-white/80">
            Share your requirement — our engineering team will get back to you with the right
            solution.
          </p>

          <ul className="mt-10 divide-y divide-white/20 border-y border-white/20">
            {details.map(({ Icon, label, value, href }) => (
              <li key={label}>
                <a
                  href={href}
                  target={href.startsWith('http') ? '_blank' : undefined}
                  rel="noreferrer"
                  className="group flex items-center gap-5 py-4"
                >
                  <span className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-white/10 ring-1 ring-white/20 transition group-hover:bg-white group-hover:text-brand-600">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-semibold uppercase tracking-wider text-white/60">{label}</span>
                    <span className="mt-0.5 block break-words font-medium">{value}</span>
                  </span>
                  <ArrowUpRight className="h-5 w-5 flex-none opacity-50 transition group-hover:opacity-100" />
                </a>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex items-center gap-4 rounded-3xl bg-white p-3 pr-6 text-ink shadow-2xl">
              <img
                src={`${process.env.PUBLIC_URL}${qrImage}`}
                alt={`QR code linking to ${company.website}`}
                className="h-24 w-24 rounded-xl"
              />
              <span>
                <span className="block font-display text-lg font-bold leading-tight">Scan to visit</span>
                <span className="mt-1 block text-xs text-gray-500">{company.website}</span>
              </span>
            </div>
            <a
              href={`https://wa.me/${company.phoneRaw.replace('+', '')}`}
              target="_blank"
              rel="noreferrer"
              className="btn bg-ink !py-4 text-white hover:bg-ink-soft"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp us
            </a>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="reveal self-start rounded-[2rem] bg-white p-7 text-ink shadow-2xl shadow-black/30 sm:p-10"
        >
          <div className="font-display text-2xl font-bold">Send an enquiry</div>
          <p className="mt-1 text-sm text-gray-500">Tell us a little about your site and requirement.</p>

          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            <Field label="Your name" name="name" required value={form.name} onChange={update} />
            <Field label="Phone number" name="phone" type="tel" required value={form.phone} onChange={update} />
            <div className="sm:col-span-2">
              <Field label="Email address" name="email" type="email" value={form.email} onChange={update} />
            </div>
          </div>

          <fieldset className="mt-8">
            <legend className="text-xs font-semibold uppercase tracking-wider text-gray-400">Interested in</legend>
            <div className="mt-3 flex flex-wrap gap-2">
              {[...products.map((p) => p.code), 'O&M', 'Other'].map((code) => {
                const selected = form.product === code;
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, product: selected ? '' : code }))}
                    aria-pressed={selected}
                    className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                      selected ? 'bg-brand-600 text-white' : 'bg-gray-100 text-ink hover:bg-gray-200'
                    }`}
                  >
                    {code}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <label className="relative mt-6 block">
            <textarea
              name="message"
              rows={4}
              placeholder="Project details"
              value={form.message}
              onChange={update}
              className={`${inputClass} resize-none`}
            />
            <span className={labelClass}>Project details — site type, capacity, timeline</span>
          </label>

          <button type="submit" className="btn-primary mt-8 w-full !py-4">
            <Send className="h-4 w-4" /> Send Enquiry
          </button>
        </form>
      </div>

      <div className="container-x relative mt-16">
        <div className="reveal overflow-hidden rounded-[2rem] bg-white p-2 shadow-2xl shadow-black/20">
          <iframe
            title="Agnisafe Engineering location"
            src={`https://www.google.com/maps?q=${encodeURIComponent(company.address)}&output=embed`}
            className="h-80 w-full rounded-[1.6rem] border-0 grayscale"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </section>
  );
}
